import { useMemo } from "react";
import { getScheduleIssues } from "../utils/issues";

// Pre-publish checklist: every scheduling problem in one ranked, clickable
// list. Errors (real conflicts) first, then warnings, then informational
// items. Clicking an issue jumps to the group or player it's about.

const SEVERITY_STYLE = {
  error: { dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", label: "Error" },
  warning: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Warning" },
  info: { dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200", label: "Info" },
};

function IssueRow({ issue, onJumpToGroup, onJumpToPlayer }) {
  const s = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.info;
  const jumpGroup = issue.groupIds && issue.groupIds.length > 0 ? issue.groupIds[0] : null;
  const clickable = jumpGroup || issue.playerId;

  function handleClick() {
    if (issue.playerId) onJumpToPlayer(issue.playerId);
    else if (jumpGroup) onJumpToGroup(jumpGroup);
  }

  return (
    <div
      onClick={clickable ? handleClick : undefined}
      className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 ${
        clickable ? "cursor-pointer hover:bg-gray-50" : ""
      }`}
    >
      <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${s.chip}`}>
            {issue.category}
          </span>
          <span className="font-medium text-gray-800 text-sm">{issue.title}</span>
        </div>
        {issue.detail && <p className="text-xs text-gray-500 mt-0.5">{issue.detail}</p>}
      </div>
      {clickable && (
        <span className="text-gray-300 text-lg leading-none mt-0.5" title="Jump to this item">›</span>
      )}
    </div>
  );
}

export default function IssuesView({ players, groups, coaches, onJumpToGroup, onJumpToPlayer }) {
  const issues = useMemo(
    () => getScheduleIssues(players, groups, coaches),
    [players, groups, coaches]
  );

  const counts = {
    error: issues.filter(i => i.severity === "error").length,
    warning: issues.filter(i => i.severity === "warning").length,
    info: issues.filter(i => i.severity === "info").length,
  };

  return (
    <div className="p-4 max-w-4xl">
      <div className="mb-4">
        <h2 className="text-base font-bold text-gray-800">Schedule issues</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Everything worth fixing before you publish the schedule. Click a row to jump to it.
        </p>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {(["error", "warning", "info"]).map(sev => {
          const s = SEVERITY_STYLE[sev];
          return (
            <div key={sev} className={`flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-sm ${s.chip}`}>
              <span className={`w-2 h-2 rounded-full ${s.dot}`} />
              <span className="font-semibold tabular-nums">{counts[sev]}</span>
              <span>{s.label}{counts[sev] !== 1 ? "s" : ""}</span>
            </div>
          );
        })}
      </div>

      {issues.length === 0 ? (
        <div className="text-center py-16 border border-gray-200 rounded-lg bg-green-50/40">
          <div className="text-4xl mb-2">✓</div>
          <p className="text-gray-700 font-medium">No issues — the schedule looks clean.</p>
          <p className="text-gray-400 text-sm mt-1">No conflicts, capacity problems, or unplaced players.</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
          {issues.map(issue => (
            <IssueRow
              key={issue.id}
              issue={issue}
              onJumpToGroup={onJumpToGroup}
              onJumpToPlayer={onJumpToPlayer}
            />
          ))}
        </div>
      )}
    </div>
  );
}
